import { useEffect, useRef } from "react";
import { Suit, type TableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, isRed, suitName, suitSymbol, suits } from "./cards";
import { trumpShown as trumpShownSig } from "./hokmReveal";
import { unitName } from "./watching";
import { HokmScene } from "../hokm/scene";
import { HokmOnline } from "../hokm/controller";
import { Overlay } from "../ui/Overlay";

/**
 * میزِ حکم — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه (HokmScene) و کنترلر (HokmOnline) پورتِ یونیتی‌اند و DOM را خودشان
 * می‌کشند؛ این کامپوننت فقط ظرف را می‌دهد و کرومِ دورِ میز را: پنجره‌ی انتخابِ
 * حکم، نوارِ مهلت، و پنجره‌های پایانِ دست و مسابقه. وضعیتِ سرور مستقیم به این
 * کامپوننت نمی‌رسد — از صفِ کنترلر می‌گذرد، تا انیمیشنِ قبلی تمام شده باشد.
 */
export function HokmTable({ state }: { state: TableState }) {
  const host = useRef<HTMLDivElement>(null);
  const ctrl = useRef<HokmOnline | null>(null);
  const [ctrlSig] = useCtrlSignal();

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: HokmOnline | null = null;
    const scene = new HokmScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      seatName: (l) => c?.name(l) ?? "حریف",
    });
    c = new HokmOnline(scene);
    ctrl.current = c;
    ctrlSig.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); ctrl.current = null; ctrlSig.value = null; };
    // صحنه یک بار ساخته می‌شود؛ وضعیت‌های بعدی از صفِ کنترلر می‌آیند.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table tv-host" ref={host}>
      <Chrome ctrl={ctrlSig} me={state.me} />
    </div>
  );
}

import { signal, type Signal } from "../net/signal";
function useCtrlSignal(): [Signal<HokmOnline | null>] {
  const ref = useRef<Signal<HokmOnline | null> | null>(null);
  if (!ref.current) ref.current = signal<HokmOnline | null>(null);
  return [ref.current];
}

/** کرومِ ری‌اکتیِ روی صحنه: پنجره‌ها و نوارِ مهلت. */
function Chrome({ ctrl, me }: { ctrl: Signal<HokmOnline | null>; me: number }) {
  const c = useSignal(ctrl);
  if (!c) return null;
  return <ChromeInner c={c} me={me} />;
}

function ChromeInner({ c, me }: { c: HokmOnline; me: number }) {
  const awaitingTrump = useSignal(c.awaitingTrump);
  const overlay = useSignal(c.overlay);
  const st = c.state;

  return (
    <>
      {awaitingTrump && !st.matchOver && (
        <Overlay label="حکم را بخوان">
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--gold-bright)" }}>حکم را بخوان</div>
          <div className="suits">
            {suits.map((s) => (
              <button key={s} className={`suitbtn ${isRed(s) ? "red" : ""}`} onClick={() => c.pickTrump(s)} aria-label={suitName[s]}>
                {suitSymbol[s]}
              </button>
            ))}
          </div>
          <div className="muted">پنج برگِ اولت را دیده‌ای؛ بقیه بعد از خواندنِ حکم پخش می‌شود.</div>
        </Overlay>
      )}

      {overlay?.kind === "hand" && (
        <Overlay label="پایانِ دست">
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--gold-bright)" }}>
            {st.watching
              ? `این دست را ${unitName(overlay.unit, st.teams)} برد`
              : overlay.unit === st.myUnit ? "این دست را بردیم" : "این دست را حریف برد"}
          </div>
          <div className="muted">{overlay.kot ? (overlay.hakemKot ? "کُتِ حاکم — " : "کُت — ") : ""}{fa(overlay.points)} امتیاز</div>
          <button className="btn" onClick={() => c.nextHand()}>دستِ بعد</button>
        </Overlay>
      )}

      {overlay?.kind === "match" && (
        <Overlay label="پایانِ مسابقه">
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--gold-bright)" }}>
            {st.watching
              ? `${unitName(overlay.unit, st.teams)} مسابقه را برد`
              : overlay.unit === st.myUnit ? "مسابقه را بردی!" : "مسابقه را باختی"}
          </div>
          <div className="muted">{st.score.map(fa).join(" - ")}</div>
          <button className="btn" onClick={() => client.exitTable()}>
            {st.watching ? "پایانِ تماشا" : "برگرد به خانه"}
          </button>
        </Overlay>
      )}
      <span hidden>{me}</span>
    </>
  );
}

export function HokmScorebar({ state }: { state: TableState }) {
  const shown = useSignal(trumpShownSig);
  const t = state.trumpChosen && shown ? state.trump : Suit.UNSPECIFIED;
  const units = state.tricksWon.length;
  // هر دو از سرور می‌آیند؛ قاعده این‌جا تکرار نمی‌شود.
  const pips = state.tricksToWin || 7;
  const mine = state.seatUnit[state.me] ?? 0;
  const teams = units === 2 && state.seats.length > units;
  const rows = units === 2 ? [mine, 1 - mine] : [mine, ...Array.from({ length: units }, (_, u) => u).filter((u) => u !== mine)];

  return (
    <div className="scorebar">
      <div className={`trumpmedal ${t === Suit.UNSPECIFIED ? "off" : ""} ${isRed(t) ? "red" : ""}`}>
        <span className="s">{t === Suit.UNSPECIFIED ? "؟" : suitSymbol[t]}</span>
        <span className="l">حکم</span>
      </div>

      <div className="rows">
        {rows.map((u, r) => (
          <div key={u} className={`srow ${r === 0 ? "us" : "them"}`}>
            <span className="who">{
              // تماشاچی «ما» ندارد: هر دو ردیف نامِ تیمِ خودش را می‌گیرد.
              state.watching ? unitName(u, teams)
                : r === 0 ? (teams ? "ما" : "من")
                : (teams ? "حریف" : state.seats[u]?.name || "حریف")
            }</span>
            <span className="pips">
              {Array.from({ length: pips }, (_, i) => {
                const on = i < (state.tricksWon[u] ?? 0);
                // کلید حالت را در خود دارد، پس نقطه‌ای که روشن می‌شود از نو
                // سوار می‌شود و انیمیشنِ تکان یک‌بار اجرا می‌شود.
                return <i key={`${i}-${on}`} className={on ? "on" : ""} />;
              })}
            </span>
            <b className="num">{fa(state.tricksWon[u] ?? 0)}</b>
          </div>
        ))}
      </div>

      <div className="match">
        <span className="l">بازی</span>
        <b>{state.score.map(fa).join(" - ")}</b>
        <span className="l">از {fa(state.matchTarget)}</span>
      </div>
    </div>
  );
}
